import { useEffect, useState } from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import {
  getVisitorId,
  setLandingAttribution,
  trackButtonClick,
  trackEvent,
  trackLandingEvent,
  setupPageTimeTracking,
} from "../lib/analytics";
import {
  DEFAULT_LANDING_PAGE_CONTENT,
  type LandingPageContent,
} from "@shared/landingPages";
import {
  buildSoftwareApplicationOffers,
  formatSoftwarePlanPrice,
  SOFTWARE_PLANS,
} from "@shared/softwarePlans";
import ManagedContentSections from "../components/lp/ManagedContentSections";
import ScriptTrackMockup from "../components/lp/ScriptTrackMockup";
import TrustBadges from "../components/lp/TrustBadges";
import ProofStory from "../components/lp/ProofStory";
import { SOFTWARE_META_DESCRIPTION } from "@shared/publicPageSeoBuilders";
import { DscsaRelatedLinks } from "../components/seo/RelatedResourceLinks";

const SOFTWARE_PATH = "/software";

function ArrowIcon() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  );
}

function FeatureStory({
  eyebrow,
  title,
  body,
  points,
  variant,
  reversed = false,
}: {
  eyebrow: string;
  title: string;
  body: string;
  points: string[];
  variant: "compliance" | "inventory" | "dispensing";
  reversed?: boolean;
}) {
  return (
    <article className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <div className="max-w-xl">
        <p className="text-sm font-semibold uppercase tracking-widest text-accent">{eyebrow}</p>
        <h3 className="text-2xl sm:text-3xl font-bold text-primary mt-3 tracking-tight">{title}</h3>
        <p className="text-gray-600 mt-4 leading-relaxed">{body}</p>
        <ul className="mt-6 space-y-3">
          {points.map((point) => (
            <li key={point} className="flex items-start gap-3 text-sm text-gray-700">
              <span className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                <svg className="w-3 h-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-3xl bg-slate-50 border border-gray-200 p-8 sm:p-10">
        <ScriptTrackMockup variant={variant} compact />
      </div>
    </article>
  );
}

export default function Software() {
  const [content, setContent] = useState<LandingPageContent>(DEFAULT_LANDING_PAGE_CONTENT.software);

  useEffect(() => {
    fetch(`/api/landing-pages/software?visitorId=${encodeURIComponent(getVisitorId())}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("managed page unavailable")))
      .then((data) => {
        if (data?.content) setContent(data.content as LandingPageContent);
        setLandingAttribution({
          landingPage: SOFTWARE_PATH,
          variantKey: data?.assignment?.variantKey || "control",
          experimentId: data?.assignment?.experimentId || undefined,
        });
        trackLandingEvent("landing_page_view", SOFTWARE_PATH);
      })
      .catch(() => {
        setLandingAttribution({ landingPage: SOFTWARE_PATH, variantKey: "control" });
        trackLandingEvent("landing_page_view", SOFTWARE_PATH);
      });
    return setupPageTimeTracking(SOFTWARE_PATH);
  }, []);

  const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];

  const handleCta = (location: string) => {
    trackLandingEvent("landing_page_cta_click", SOFTWARE_PATH, {
      buttonLabel: content.ctaText,
      buttonLocation: location,
    });
    trackButtonClick(content.ctaText, location);
  };

  return (
    <div className="landing-pages-group">
      <SEO
        title="ScriptTrack — Medication Tracking Software"
        description={SOFTWARE_META_DESCRIPTION}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Software", path: SOFTWARE_PATH },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "ScriptTrack",
          applicationCategory: "HealthApplication",
          operatingSystem: "Web",
          description: SOFTWARE_META_DESCRIPTION,
          url: "https://premierpharma.com/software",
          offers: buildSoftwareApplicationOffers(priceValidUntil),
          publisher: { "@id": "https://premierpharma.com/#organization" },
        }}
      />

      <section className="lp-software-hero relative overflow-hidden py-12 lg:py-20 border-b border-[hsl(var(--line))]">
        <div className="absolute right-0 top-0 h-full w-[42%] bg-primary hidden lg:block" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
            <div className="flex flex-col gap-6 landing-reveal lp-hero-copy">
              <div className="lp-hero-kicker inline-flex items-center gap-2 bg-accent/10 border border-accent/20 text-accent-700 text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full w-fit">
                ScriptTrack / workflow software
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[4.4rem] font-bold leading-[.97] tracking-[-.05em] max-w-xl">{content.headline}</h1>
              <p className="text-primary/70 text-lg leading-relaxed max-w-xl">{content.subheadline}</p>
              <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center mt-1">
                <Link
                  to={content.ctaDestination}
                  onClick={() => handleCta("software-hero")}
                  className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-600 active:bg-accent-600 text-white font-bold text-base px-8 py-4 rounded-btn shadow-lg shadow-primary/20 transition-colors duration-200 lp-hero-cta"
                >
                  {content.ctaText}
                  <ArrowIcon />
                </Link>
                <a
                  href="#capabilities"
                  onClick={() => {
                    trackEvent("software_exploration", { location: "software-hero", action: "capabilities" });
                    trackLandingEvent("software_exploration", SOFTWARE_PATH, { location: "software-hero", action: "capabilities" });
                  }}
                  className="lp-hero-secondary inline-flex items-center justify-center text-primary/75 hover:text-primary font-semibold text-sm px-2 py-3 underline underline-offset-4 transition-colors"
                >
                  Explore capabilities
                </a>
              </div>
               <TrustBadges variant="dark" className="justify-start" signals={content.trustSignals} />
              <p className="text-primary/55 text-sm">{content.reassurance}</p>
            </div>
            <div className="relative z-10 flex justify-center lg:justify-end landing-reveal-delay">
              <ScriptTrackMockup variant="compliance" />
            </div>
          </div>
        </div>
      </section>

      <div className="lg:hidden sticky top-16 z-40 bg-white/95 backdrop-blur border-b border-gray-200 px-4 py-3">
        <Link to={content.ctaDestination} onClick={() => handleCta("software-mobile-bar")} className="btn-primary w-full inline-flex justify-center py-3">
          {content.ctaText}
        </Link>
      </div>

      <ProofStory
        pagePath={SOFTWARE_PATH}
        proofKind="software"
        eyebrow="One workflow, then the next"
        headline="See where medication work becomes visible."
        intro="Start with the included compliance workflow. When operations expand, optional products can extend the same account into inventory, dispensing, prescribing, and billing."
        mockupVariant="inventory"
        steps={[
          { label: "Start", title: "Organize compliance records", body: "Begin with transaction information, receiving review, and visible next steps for a verified account." },
          { label: "Expand", title: "Add inventory operations", body: "Bring product movement and inventory work into a broader operational view when the team needs it." },
          { label: "Extend", title: "Support medication workflows", body: "Optional plans can add dispensing, prescribing, or billing capabilities for eligible practices." },
        ]}
      />

      <section className="bg-white py-12" aria-labelledby="software-resources-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-6">
            <p className="text-sm font-semibold uppercase tracking-widest text-accent">Go deeper</p>
            <h2 id="software-resources-heading" className="text-2xl sm:text-3xl font-bold text-primary mt-2 tracking-tight">Connect the workflow to the requirement</h2>
            <p className="text-gray-600 mt-3 leading-relaxed">Read the DSCSA guidance behind the receiving, transaction-record, and traceability workflows shown above.</p>
          </div>
          <DscsaRelatedLinks />
        </div>
      </section>

       <section id="capabilities" className="bg-white py-16 lg:py-24 scroll-mt-16" aria-labelledby="capabilities-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-accent">A product family, not a promise pile</p>
            <h2 id="capabilities-heading" className="text-3xl sm:text-4xl font-bold text-primary mt-3 tracking-tight">Follow the work from records to operations</h2>
            <p className="text-gray-600 mt-4 leading-relaxed">Each view below represents a workflow category described in the available product plans. It is an illustration of where the product can fit, not a live account screenshot.</p>
          </div>
           <div className="mt-14 space-y-20 lg:space-y-28">
            <FeatureStory
              eyebrow="Included starter workflow"
              title="Start with transaction and receiving organization"
              body="The included starter compliance software gives verified accounts a place to organize transaction information, receiving review, and next steps."
              points={SOFTWARE_PLANS[0].features}
              variant="compliance"
            />
            <FeatureStory
              eyebrow={SOFTWARE_PLANS[1].name}
              title="Add inventory operations when the team needs them"
              body={SOFTWARE_PLANS[1].desc}
              points={SOFTWARE_PLANS[1].features}
              variant="inventory"
              reversed
            />
            <FeatureStory
              eyebrow={`${SOFTWARE_PLANS[2].name} and ${SOFTWARE_PLANS[3].name}`}
              title="Extend into dispensing, prescribing, and billing workflows"
              body="The paid plans add progressively broader workflows for practices that need dispensing, e-prescribing, or insurance billing capabilities."
              points={[...SOFTWARE_PLANS[2].features.slice(1), ...SOFTWARE_PLANS[3].features.slice(1)]}
              variant="dispensing"
            />
          </div>
        </div>
      </section>

       <section className="bg-[hsl(var(--paper))] py-16 lg:py-24" aria-labelledby="fit-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-accent">Designed for licensed businesses</p>
              <h2 id="fit-heading" className="text-3xl sm:text-4xl font-bold text-primary mt-3 tracking-tight">A practical fit for teams with medication workflows</h2>
              <p className="text-gray-600 mt-4 leading-relaxed">Licensed pharmacies, clinics, medical practices, and veterinary businesses can apply. Credential review is required before activation, and the free entry point does not require a purchase.</p>
              <Link to="/about" onClick={() => trackEvent("software_exploration", { location: "audience-fit", action: "premier-context" })} className="inline-flex items-center gap-2 text-accent font-semibold mt-6 hover:text-accent-600">
                Learn about Premier Pharma <ArrowIcon />
              </Link>
            </div>
             <div className="bg-primary text-white border border-primary rounded-3xl p-7 sm:p-9 shadow-sm">
               <p className="text-xs font-semibold uppercase tracking-widest text-accent-200">Premier Pharma context</p>
               <p className="text-xl font-semibold text-white mt-4 leading-relaxed">Built alongside a NABP-accredited wholesale pharmaceutical distributor.</p>
               <p className="text-sm text-white/70 mt-4 leading-relaxed">Premier Pharma’s founder is a pharmacist and software engineer. That pharmacy context informs the product conversation without turning the page into a certification or outcome claim.</p>
               <div className="mt-6 pt-6 border-t border-white/15">
                 <TrustBadges variant="dark" className="justify-start" signals={["Credential review required", "Optional paid products", "Human setup support"]} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="paid-options" className="bg-white py-16 lg:py-24 scroll-mt-16" aria-labelledby="plans-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-accent">Transparent plan path</p>
            <h2 id="plans-heading" className="text-3xl sm:text-4xl font-bold text-primary mt-3 tracking-tight">Start free, then compare optional products</h2>
            <p className="text-gray-600 mt-4 leading-relaxed">The same verified account is the starting point. Paid plan pricing below comes directly from the current software plan source and is discussed after eligibility review.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SOFTWARE_PLANS.map((plan) => (
              <article key={plan.name} className={`relative flex flex-col rounded-2xl border p-6 ${plan.included ? "border-accent bg-emerald-50/40" : "border-gray-200 bg-white"} ${plan.popular ? "ring-2 ring-accent/30" : ""}`}>
                {plan.popular && <span className="absolute -top-3 left-5 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full">Most complete cash workflow</span>}
                <p className="text-xs font-semibold uppercase tracking-wide text-accent">{plan.audience}</p>
                <h3 className="font-bold text-primary text-xl mt-3">{plan.name}</h3>
                <p className="text-gray-600 text-sm mt-2 min-h-10">{plan.desc || "Included starter workflow for verified accounts."}</p>
                <p className="mt-5 text-2xl font-bold text-primary">{formatSoftwarePlanPrice(plan)}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                      <svg className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 20 20" aria-hidden="true">
                        <path fill="currentColor" fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-500 mt-6 pt-4 border-t border-gray-200">
                  {plan.included ? "Create an account to start." : "Optional after verification."}
                </p>
              </article>
            ))}
          </div>
          <div className="mt-10 rounded-2xl bg-primary text-white p-7 sm:p-9 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-300">One next step</p>
              <h3 className="text-2xl font-bold mt-2">See whether ScriptTrack fits your practice</h3>
              <p className="text-white/70 text-sm mt-2">{content.reassurance}</p>
            </div>
            <Link to={content.ctaDestination} onClick={() => handleCta("plans-final")} className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-6 py-3.5 rounded-btn whitespace-nowrap">
              {content.ctaText} <ArrowIcon />
            </Link>
          </div>
        </div>
      </section>

      <ManagedContentSections content={content} pageKind="software" />
    </div>
  );
}